import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { rand, monthLabel, dateLabel, MONTHS } from '@/components/stokvel/format';

const pad = (n) => String(n).padStart(2, '0');
const ymOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
export const payrollPeriodLabel = (ym) => {
  const [, m] = ym.split('-').map(Number);
  return `${monthLabel(ym)} payroll (26 ${MONTHS[(m + 10) % 12]} – 25 ${MONTHS[m - 1]})`;
};
const currentPayrollMonth = () => {
  const d = new Date();
  return d.getDate() > 25 ? ymOf(new Date(d.getFullYear(), d.getMonth() + 1, 1)) : ymOf(d);
};
const payrollOptions = () => {
  const [y, m] = currentPayrollMonth().split('-').map(Number);
  return Array.from({ length: 12 }, (_, i) => ymOf(new Date(y, m - 4 + i, 1)));
};

export const run = async (fn, ok, onDone, onClose, setBusy) => {
  setBusy(true);
  try { await fn(); toast.success(ok); onClose(); onDone(); }
  catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  finally { setBusy(false); }
};

export const MembershipDecisionDialog = ({ item, onClose, onDone }) => {
  const [firstMonth, setFirstMonth] = useState(currentPayrollMonth());
  const [company, setCompany] = useState('0');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [reasonErr, setReasonErr] = useState(false);
  useEffect(() => { setFirstMonth(currentPayrollMonth()); setCompany('0'); setReason(''); setReasonErr(false); }, [item]);
  const decide = (action) => {
    if (action === 'decline' && !reason.trim()) {
      setReasonErr(true);
      toast.error('Please enter a reason for declining');
      document.querySelector('[data-testid="stokvel-decision-reason-input"]')?.focus();
      return;
    }
    run(
    () => axios.post(`${API}/stokvel/admin/memberships/${item.id}/decision`, {
      action, reason, first_month: firstMonth, company_amount: Number(company) || 0,
    }),
    action === 'approve' ? 'Application approved' : 'Application declined', onDone, onClose, setBusy);
  };
  return (
    <Dialog open={!!item} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="stokvel-decision-dialog">
        <DialogHeader><DialogTitle>Review application — {item?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p>Monthly deduction: <b>{rand(item?.monthly_amount)}</b> · applied {dateLabel(item?.applied_at)}</p>
          <div>
            <Label className="text-xs">First deduction payroll month (required to approve)</Label>
            <Select value={firstMonth} onValueChange={setFirstMonth}>
              <SelectTrigger data-testid="stokvel-first-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {payrollOptions().map(ym => <SelectItem key={ym} value={ym}>{payrollPeriodLabel(ym)}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-stone-500 mt-1">Deducted on the 25th of that month. Earlier months can be added later in the ledger (late opt-in).</p>
          </div>
          <div><Label className="text-xs">Monthly company contribution for this member (R)</Label>
            <Input type="number" min="0" value={company} onChange={e => setCompany(e.target.value)} data-testid="stokvel-company-amount-input" /></div>
          <div><Label className="text-xs">Reason / note (required to decline)</Label>
            <Textarea rows={3} value={reason} onChange={e => { setReason(e.target.value); setReasonErr(false); }}
              className={reasonErr ? 'border-rose-500 ring-1 ring-rose-300' : ''} data-testid="stokvel-decision-reason-input" />
            {reasonErr && <p className="text-xs text-rose-600 mt-1" data-testid="stokvel-decision-reason-error">A reason is required to decline — it is sent to the staff member.</p>}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={busy} onClick={() => decide('decline')} className="text-rose-700" data-testid="stokvel-decline-btn">Decline</Button>
            <Button disabled={busy || !firstMonth} onClick={() => decide('approve')} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="stokvel-approve-btn">Approve</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ExitDialog = ({ item, exitTypes, onClose, onDone }) => {
  const [type, setType] = useState('resignation');
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [penalty, setPenalty] = useState('0');
  const [refund, setRefund] = useState('');
  const [refundDate, setRefundDate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [pv, setPv] = useState(null);
  const t = pv?.totals || {};
  const forfeits = type !== 'no_fault';
  useEffect(() => {
    if (!item || !endDate) return;
    axios.get(`${API}/stokvel/admin/memberships/${item.id}/exit-preview`, { params: { exit_type: type, end_date: endDate } })
      .then(r => { setPv(r.data); setPenalty(r.data.penalty.toFixed(2)); setRefund(r.data.refund.toFixed(2)); })
      .catch(() => setPv(null));
  }, [item, type, endDate]);
  const save = () => run(
    () => axios.post(`${API}/stokvel/admin/memberships/${item.id}/exit`, {
      exit_type: type, end_date: endDate, penalty_amount: Number(penalty) || 0,
      refund_amount: Number(refund) || 0, refund_date: refundDate || endDate, note,
    }), 'Exit recorded', onDone, onClose, setBusy);
  return (
    <Dialog open={!!item} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="stokvel-exit-dialog">
        <DialogHeader><DialogTitle>Record exit — {item?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-xs">Exit type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="stokvel-exit-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(exitTypes || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v.label} ({v.penalty_pct}%)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="bg-stone-50 rounded p-2 text-xs text-stone-700 space-y-0.5">
            <div>Capital contributed up to {pv?.last_month || '…'}: <b>{rand(t.contribution)}</b> · withdrawals {rand(t.withdrawal)}</div>
            <div>Interest {rand(t.interest)} · company contributions {rand(t.company)}
              {forfeits ? <b className="text-rose-700"> → forfeited to the penalty pot</b> : <b className="text-emerald-700"> → paid to member</b>}</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Last working / end date</Label><Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="stokvel-exit-date-input" /></div>
            <div><Label className="text-xs">Penalty fee (R) → penalty pot</Label><Input type="number" min="0" value={penalty} onChange={e => setPenalty(e.target.value)} data-testid="stokvel-exit-penalty-input" /></div>
            <div><Label className="text-xs">Refund paid to member (R)</Label><Input type="number" min="0" value={refund} onChange={e => setRefund(e.target.value)} data-testid="stokvel-exit-refund-input" /></div>
            <div><Label className="text-xs">Refund date</Label><Input type="date" value={refundDate} onChange={e => setRefundDate(e.target.value)} data-testid="stokvel-exit-refund-date-input" /></div>
          </div>
          <div><Label className="text-xs">Note</Label><Input value={note} onChange={e => setNote(e.target.value)} data-testid="stokvel-exit-note-input" /></div>
          <p className="text-xs text-stone-500">Deductions stop after the last payday before the end date (if not already stopped). Penalty and refund are booked as ledger lines you can edit later.</p>
          <div className="flex justify-end"><Button disabled={busy} onClick={save} className="bg-rose-700 text-white hover:bg-rose-800" data-testid="stokvel-exit-save-btn">Record exit</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
